import type { ReactNode } from 'react'
import { Card, CardContent } from './components/shared'
import StartStep from './components/StartStep'
import PasswordLoginStep from './components/PasswordLoginStep'
import LoginOtpStep from './components/LoginOtpStep'
import JoinStep from './components/JoinStep'
import JoinOtpStep from './components/JoinOtpStep'
import SuccessStep from './components/SuccessStep'
import { useChatEmbeddedAccountAccess } from './useChatEmbeddedAccountAccess'
import type { Props } from './types'

function ChatEmbeddedAccountAccess(props: Props) {
  const {
    view,
    countDown,
    resendLabel,
    processing,
    error,
    isWhitelabel,
    currentRegionCode,
    defaultContact,
    recaptchaRef,
    handlers,
    isActionPending,
  } = useChatEmbeddedAccountAccess(props)

  const isLastMessage = props.isLastMessage ?? false

  let content: ReactNode
  switch (view.mode) {
    case 'start':
      content = (
        <StartStep
          autoFocus={isLastMessage}
          error={error}
          processing={processing}
          recaptchaRef={recaptchaRef}
          onCheckUser={handlers.checkUser}
          onPrivateAppleEmailLogin={handlers.privateAppleEmailLogin}
          onSocialInteractionCapture={handlers.socialInteractionCapture}
          isActionPending={isActionPending}
        />
      )
      break
    case 'loginPassword':
    case 'loginPasswordOTP':
      content = (
        <PasswordLoginStep
          mode={view.mode}
          user={view.user}
          autoFocus={isLastMessage}
          error={error}
          processing={processing}
          recaptchaRef={recaptchaRef}
          onBack={handlers.backToStart}
          onPasswordLogin={handlers.passwordLogin}
          onForgotPassword={handlers.forgotPassword}
          onSendLoginOtp={handlers.sendLoginOtp}
          isActionPending={isActionPending}
        />
      )
      break
    case 'loginOTP':
      content = (
        <LoginOtpStep
          user={view.user}
          autoFocus={isLastMessage}
          error={error}
          processing={processing}
          countDown={countDown}
          resendLabel={resendLabel}
          defaultContact={defaultContact}
          onBack={handlers.showLoginPasswordOtp}
          onSubmit={handlers.submitLoginOtp}
          onResend={handlers.resendLoginOtp}
          onRequestCall={handlers.requestCallLoginOtp}
          isActionPending={isActionPending}
        />
      )
      break
    case 'join':
      content = (
        <JoinStep
          user={view.user}
          autoFocus={isLastMessage}
          error={error}
          processing={processing}
          isEmailSignUp={view.isEmailSignUp}
          isWhitelabel={isWhitelabel}
          currentRegionCode={currentRegionCode}
          recaptchaRef={recaptchaRef}
          onBack={handlers.backToStart}
          onSubmit={handlers.join}
          isActionPending={isActionPending}
        />
      )
      break
    case 'joinOTP':
      content = (
        <JoinOtpStep
          pendingSignUpOTPData={view.pendingSignUpOTPData}
          autoFocus={isLastMessage}
          error={error}
          processing={processing}
          countDown={countDown}
          resendLabel={resendLabel}
          defaultContact={defaultContact}
          onBack={handlers.showJoin}
          onSubmit={handlers.submitSignUpOtp}
          onResend={handlers.resendSignUpOtp}
          onRequestCall={handlers.requestCallSignUpOtp}
          isActionPending={isActionPending}
        />
      )
      break
    case 'success':
      content = <SuccessStep />
      break
    case 'unavailable':
      content = undefined
      break
  }

  if (view.mode === 'success') {
    return content
  }

  return (
    <Card>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  )
}

export default ChatEmbeddedAccountAccess
